import { NotificationType } from '~/app/enums/NotificationId';
import { PrescriptionStatus } from '~/app/enums/PrescriptionStatus';
import { Configuration } from '~/app/models/Configuration';
import { TherapyJobV2 } from '~/app/models/TherapyJobV2';
import { getDuration } from '~/app/pipes/TimeAgo';
import { AcknowledgeItem } from '~/app/services/activity-log.service';
import { NotificationsStore } from '~/app/store/notification.store';
import { flowRateDifference } from '../flowRateDifference';
import { volumeDifference } from '../VolumeDifference';
import { abortTriggered } from './AbortTriggered';
import { detachedDripChamber } from './DetachedDripChamber';
import { enabledBENotifications } from './enabledBENotifications';
import { flowRateTooFast } from './FlowRateTooFast';
import { flowRateTooSlow } from './FlowRateTooSlow';
import { createNotification } from './NotificationBuilder';
import { noVolumeProvided } from './NoVolumeProvided';
import { pendingConfirmationInfo } from './PendingConfirmationInfo';
import { pendingConfirmationWarning } from './PendingConfirmationWarning';
import { jobNotResolved } from './TherapyJobNotResolved';
import { therapyJobUpdated } from './TherapyJobUpdated';
import { unpairOnRunning } from './UnpairedOnRunning';
import { volumeTooHigh } from './VolumeTooHigh';

type Args = [NotificationsStore, Partial<TherapyJobV2>, boolean, boolean?, Configuration?, string?];

const notificationFunctions = {
  [NotificationType.THERAPY_JOB_UNPAIRED_FROM_MD]: (...args: Args) => {
    const [store, tj, notified, notify] = args;
    if (!tj) {
      return;
    }

    const condition = notify || unpairOnRunning(tj);

    createNotification(tj)
      .type(NotificationType.THERAPY_JOB_UNPAIRED_FROM_MD)
      .showAlarmCenter(false)
      .buildAndPatch(condition, store, notified);
  },

  [NotificationType.PENDING_CONFIRMATION_INFO]: (...args: Args) => {
    const [store, tj, notified, notify, _config, locale]: Args = args;
    if (!tj) {
      return;
    }

    const condition = notify || pendingConfirmationInfo(tj);

    createNotification(tj)
      .type(NotificationType.PENDING_CONFIRMATION_INFO)
      .showAlarmCenter(false)
      .addTranslationParam('deviceId', tj.monitorDeviceId)
      .addTranslationParam('timeAgo', getDuration(tj.endTime, locale))
      .buildAndPatch(condition, store, notified);
  },
  [NotificationType.PENDING_CONFIRMATION_WARNING]: (...args: Args) => {
    const [store, tj, notified, notify, _config, locale]: Args = args;
    if (!tj) {
      return;
    }

    const condition = notify || pendingConfirmationWarning(tj);

    createNotification(tj)
      .type(NotificationType.PENDING_CONFIRMATION_WARNING)
      .showAlarmCenter(false)
      .addTranslationParam('deviceId', tj.monitorDeviceId)
      .addTranslationParam('timeAgo', getDuration(tj.endTime, locale))
      .buildAndPatch(condition, store, notified);
  },

  // will move out but is not implemented in BE yet
  [NotificationType.THERAPY_JOB_ABORT_TRIGGERED]: (...args: Args) => {
    const [store, tj, notified, notify] = args;
    if (!tj) {
      return;
    }

    const condition = notify || abortTriggered(tj);

    createNotification(tj)
      .type(NotificationType.THERAPY_JOB_ABORT_TRIGGERED)
      .showAlarmCenter(true)
      .addTranslationParam('deviceId', tj.monitorDeviceId)
      .buildAndPatch(condition, store, notified);
  },

  // will move out but is not implemented in BE yet
  [NotificationType.VOLUME_TOO_HIGH]: (...args: Args) => {
    const [store, tj, notified, notify, config] = args;
    if (!tj) {
      return;
    }

    const volumeConfig = config?.monitoringSettings?.volumeDeviation;
    const enableAlert = volumeConfig?.enableAlert;
    const deviation = volumeConfig?.deviationPercentage;

    const notification = createNotification(tj).type(NotificationType.VOLUME_TOO_HIGH).showAlarmCenter(true);

    if (!enableAlert) {
      // if the alert is not enabled, we make sure to remove any existing notification
      notification.buildAndPatch(false, store, notified);
      return;
    }

    const totalVolume = tj?.totalVolumeEdited || tj?.totalVolumePrescribed;
    if (!totalVolume) {
      // if no total volume is provided, we cannot calculate the deviation, so we skip showing the notification
      notification.buildAndPatch(false, store, notified);
      return;
    }

    const condition = notify || volumeTooHigh(tj, deviation);

    notification.addTranslationParam('volume', volumeDifference(tj)).buildAndPatch(condition, store, notified);
  },
  [NotificationType.NO_VOLUME_PROVIDED]: (...args: Args) => {
    const [store, tj, notified, notify] = args;
    if (!tj) {
      return;
    }

    const condition = notify || noVolumeProvided(tj);

    createNotification(tj)
      .type(NotificationType.NO_VOLUME_PROVIDED)
      .showAlarmCenter(false)
      .buildAndPatch(condition, store, notified);
  },
  [NotificationType.DRIP_CHAMBER_DETACHED]: (...args: Args) => {
    const [store, tj, notified, notify] = args;
    if (!tj) {
      return;
    }

    const condition = notify || detachedDripChamber(tj);

    createNotification(tj)
      .type(NotificationType.DRIP_CHAMBER_DETACHED)
      .showAlarmCenter(true)
      .addTranslationParam('deviceId', tj.monitorDeviceId)
      .buildAndPatch(condition, store, notified);
  },
  [NotificationType.FLOW_RATE_TOO_FAST]: (...args: Args) => {
    const [store, tj, notified, notify, config] = args;
    if (!tj) {
      return;
    }

    const flowRateConfig = config?.monitoringSettings?.flowRateDeviation;
    const enableAlert = flowRateConfig?.enableAlert;
    const flowRateDeviationPercentage = flowRateConfig?.deviationPercentage;

    const notification = createNotification(tj).type(NotificationType.FLOW_RATE_TOO_FAST).showAlarmCenter(true);

    if (!enableAlert) {
      // if the alert is not enabled, we make sure to remove any existing notification
      notification.buildAndPatch(false, store, notified);
      return;
    }

    const flowRatePlanned = tj?.flowRatePlannedEdited || tj?.flowRatePlannedPrescribed;
    if (!flowRatePlanned) {
      // if no flow rate is provided, we cannot calculate the deviation, so we skip showing the notification
      notification.buildAndPatch(false, store, notified);
      return;
    }

    const condition = notify || flowRateTooFast(tj, flowRateDeviationPercentage);

    notification.addTranslationParam('flowRate', flowRateDifference(tj)).buildAndPatch(condition, store, notified);
  },
  [NotificationType.FLOW_RATE_TOO_SLOW]: (...args: Args) => {
    const [store, tj, notified, notify, config] = args;
    if (!tj) {
      return;
    }

    const flowRateConfig = config?.monitoringSettings?.flowRateDeviation;
    const enableAlert = flowRateConfig?.enableAlert;
    const flowRateDeviationPercentage = flowRateConfig?.deviationPercentage;

    const notification = createNotification(tj).type(NotificationType.FLOW_RATE_TOO_SLOW).showAlarmCenter(true);

    if (!enableAlert) {
      // if the alert is not enabled, we make sure to remove any existing notification
      notification.buildAndPatch(false, store, notified);
      return;
    }

    const flowRatePlanned = tj?.flowRatePlannedEdited || tj?.flowRatePlannedPrescribed;
    if (!flowRatePlanned) {
      // if no flow rate is provided, we cannot calculate the deviation, so we skip showing the notification
      notification.buildAndPatch(false, store, notified);
      return;
    }

    const condition = notify || flowRateTooSlow(tj, flowRateDeviationPercentage);

    notification.addTranslationParam('flowRate', flowRateDifference(tj)).buildAndPatch(condition, store, notified);
  },
  [NotificationType.THERAPY_JOB_UPDATED]: (...args: Args) => {
    const [store, tj, notified, notify] = args;
    if (!tj) {
      return;
    }

    const condition = notify || therapyJobUpdated(store, tj);

    createNotification(tj)
      .type(NotificationType.THERAPY_JOB_UPDATED)
      .showAlarmCenter(false)
      .buildAndPatch(condition, store, notified);
  },
  [NotificationType.THERAPY_JOB_MARK_FOR_DELETION]: (...args: Args) => {
    const [store, tj, notified, notify] = args;
    if (!tj) {
      return;
    }

    const acknowledgement: AcknowledgeItem = store.acknowledgments()?.[tj.id];
    const condition =
      notify ||
      (tj.prescriptionStatus === PrescriptionStatus.ACTIVE && acknowledgement?.therapyJobDeleted && jobNotResolved(tj));

    createNotification(tj)
      .type(NotificationType.THERAPY_JOB_MARK_FOR_DELETION)
      .showAlarmCenter(true)
      .buildAndPatch(condition, store, notified);
  },
  [NotificationType.THERAPY_JOB_ON_HOLD]: (...args: Args) => {
    const [store, tj, notified, notify] = args;
    if (!tj) {
      return;
    }

    const condition = notify || (tj.prescriptionStatus === PrescriptionStatus.ON_HOLD && jobNotResolved(tj));

    createNotification(tj)
      .type(NotificationType.THERAPY_JOB_ON_HOLD)
      .showAlarmCenter(true)
      .buildAndPatch(condition, store, notified);
  },
  [NotificationType.THERAPY_JOB_CANCELLED]: (...args: Args) => {
    const [store, tj, notified, notify] = args;
    if (!tj) {
      return;
    }

    const condition = notify || (tj.prescriptionStatus === PrescriptionStatus.CANCELLED && jobNotResolved(tj));

    createNotification(tj)
      .type(NotificationType.THERAPY_JOB_CANCELLED)
      .showAlarmCenter(true)
      .buildAndPatch(condition, store, notified);
  },
  [NotificationType.THERAPY_JOB_STOPPED]: (...args: Args) => {
    const [store, tj, notified, notify] = args;
    if (!tj) {
      return;
    }

    const condition = notify || (tj.prescriptionStatus === PrescriptionStatus.STOPPED && jobNotResolved(tj));

    createNotification(tj)
      .type(NotificationType.THERAPY_JOB_STOPPED)
      .showAlarmCenter(true)
      .buildAndPatch(condition, store, notified);
  },
  [NotificationType.THERAPY_JOB_ENTERED_IN_ERROR]: (...args: Args) => {
    const [store, tj, notified, notify] = args;
    if (!tj) {
      return;
    }

    const condition = notify || (tj.prescriptionStatus === PrescriptionStatus.ENTERED_IN_ERROR && jobNotResolved(tj));

    createNotification(tj)
      .type(NotificationType.THERAPY_JOB_ENTERED_IN_ERROR)
      .showAlarmCenter(true)
      .buildAndPatch(condition, store, notified);
  },
  [NotificationType.DEVICE_DISCONNECTED]: (...args: Args) => {
    const [store, tj, notified, notify] = args;
    if (!tj) {
      return;
    }

    // this notification will be only BE notification
    const condition = notify;

    createNotification(tj)
      .type(NotificationType.DEVICE_DISCONNECTED)
      .showAlarmCenter(true)
      .buildAndPatch(condition, store, notified);
  },
  [NotificationType.INFUSION_IN_ERROR]: (...args: Args) => {
    const [store, tj, notified, notify] = args;
    if (!tj) {
      return;
    }

    // this notification will be only BE notification
    const condition = notify;

    createNotification(tj)
      .type(NotificationType.INFUSION_IN_ERROR)
      .showAlarmCenter(true)
      .buildAndPatch(condition, store, notified);
  },
  [NotificationType.PATIENT_DISCHARGED]: (...args: Args) => {
    const [store, tj, notified, notify] = args;
    if (!tj) {
      return;
    }

    const condition = notify || (!!tj.patientDischarged && jobNotResolved(tj));

    createNotification(tj)
      .type(NotificationType.PATIENT_DISCHARGED)
      .showAlarmCenter(true)
      .buildAndPatch(condition, store, notified);
  },
  [NotificationType.PATIENT_NOT_ADMITTED]: (...args: Args) => {
    const [store, tj, notified, notify] = args;
    if (!tj) {
      return;
    }

    const condition = notify || (!!tj.patientNotAdmitted && jobNotResolved(tj));

    createNotification(tj)
      .type(NotificationType.PATIENT_NOT_ADMITTED)
      .showAlarmCenter(true)
      .buildAndPatch(condition, store, notified);
  }
};

/*
  FE notifications are build checking conditions criteria to show or hide them
*/
export const checkAllNotificationConditionsPerJob = (
  therapyJob: Partial<TherapyJobV2>,
  store,
  configuration,
  locale?: string
) => {
  if (!therapyJob) {
    console.warn('No therapy job provided to check notifications conditions.');
    return;
  }
  // in FE we iterate over all the cases to find if some criteria is met
  Object.keys(notificationFunctions).forEach((key: NotificationType) => {
    // we only process the FE notifications
    if (!enabledBENotifications.includes(key)) {
      const notified = !!store.entityMap()?.[`${key}-${therapyJob.id}`];
      const notifyAnyways = false;

      notificationFunctions[key](store, therapyJob, notified, notifyAnyways, configuration, locale);
    }
  });
};

/*
  build one type of notification for a therapy job
*/
export const buildNotification = (
  notificationType: NotificationType,
  job: Partial<TherapyJobV2>,
  store,
  configuration,
  notifyAnyways = false,
  locale?: string
) => {
  if (!job?.id || !notificationType) {
    return;
  }

  const notified = !!store.entityMap()?.[`${notificationType}-${job.id}`];
  notificationFunctions[notificationType](store, job, notified, notifyAnyways, configuration, locale);
};
